import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map, mapTo, share } from 'rxjs/operators';
import { PagedResponse } from '../core';
import {
  GetSalesInvoiceRequest,
  HostService,
  ListSalesInvoiceRequest,
  PagedRequestOptions,
  PostSalesInvoiceRequest,
  SalesInvoiceApiService,
} from '@vendasta/billing';
import {
  listSalesInvoiceFiltersToApi,
  ListSalesInvoicesFilters,
  SalesInvoice,
  salesInvoiceFromApi,
} from './sales-invoice';

@Injectable({ providedIn: 'root' })
export class SalesInvoiceService {
  constructor(
    private hostService: HostService,
    private http: HttpClient,
    private salesInvoiceApi: SalesInvoiceApiService,
  ) {}

  downloadPdf(salesInvoiceId: string, merchantId?: string): Observable<Blob> {
    let params;
    if (merchantId) {
      params = { salesInvoiceId, merchantId };
    } else {
      params = { salesInvoiceId };
    }
    return this.http
      .get(`${this.hostService.hostWithScheme}/sales-invoice-pdf`, {
        observe: 'body',
        params: params,
        responseType: 'blob',
        withCredentials: true,
      })
      .pipe(share(), catchError(handleError));
  }

  listSalesInvoices(
    filters: ListSalesInvoicesFilters,
    cursor: string,
    pageSize: number,
    searchTerm?: string,
  ): Observable<PagedResponse<SalesInvoice>> {
    const req = new ListSalesInvoiceRequest({
      filters: listSalesInvoiceFiltersToApi(filters),
      pagingOptions: new PagedRequestOptions({ cursor, pageSize }),
      searchTerm: searchTerm || '',
    });
    return this.salesInvoiceApi.list(req).pipe(
      map((response) => {
        const salesInvoices = (response.invoice || []).map((i) => salesInvoiceFromApi(i));
        return new PagedResponse(salesInvoices, response.pagingMetadata.nextCursor, response.pagingMetadata.hasMore);
      }),
    );
  }

  postSalesInvoice(invoice_id: string): Observable<boolean> {
    const req = new PostSalesInvoiceRequest({
      invoiceId: invoice_id,
    });
    return this.salesInvoiceApi.post(req).pipe(mapTo(true));
  }

  getSalesInvoice(invoice_id: string, merchant_id: string): Observable<SalesInvoice> {
    const req = new GetSalesInvoiceRequest({
      invoiceId: invoice_id,
      merchantId: merchant_id,
    });
    return this.salesInvoiceApi.get(req).pipe(map((resp) => salesInvoiceFromApi(resp.invoice)));
  }
}

function handleError(error: HttpErrorResponse): Observable<never> {
  if (error.error instanceof ErrorEvent) {
    console.error(`An error occurred:`, error.error.message);
  } else {
    console.error(`Server returned ${error.message}`);
  }

  return observableThrowError(`Unable to download pdf`);
}
